'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Copy, Check, LogIn } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Shikues Free', email: 'visar.berisha@cinestream.al', password: 'Cine2026!' },
  { role: 'Abonent Premium', email: 'arta.hoxha@cinestream.al', password: 'Premium2026#' },
  { role: 'Admin', email: 'admin@cinestream.al', password: 'Admin@Cine26' },
];

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setSubmitting(true);
    // backend: POST /api/auth/login with email + password
    await new Promise((r) => setTimeout(r, 1400));
    const validCredential = DEMO_CREDENTIALS.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!validCredential) {
      setError('email', {
        message: 'Kredenciale të gabuara — përdorni llogaritë demo më poshtë për t\'u kyçur',
      });
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    toast.success(`Mirë se vini, ${validCredential.role}!`);
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseCredential = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-800 text-foreground">Hyr në Llogari</h1>
        <p className="text-sm text-secondary-foreground mt-1">
          Mirë se vini sërish në Dublime Al Shqip
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-xs font-600 text-secondary-foreground mb-1.5">
            Adresa Email
          </label>
          <input
            type="email"
            {...register('email', {
              required: 'Email-i është i detyrueshëm',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Adresë email-i e pavlefshme' },
            })}
            placeholder="ju@shembull.al"
            className={`w-full px-3 py-2.5 bg-input border ${
              errors.email ? 'border-red-500/50 focus:ring-red-500/30' : 'border-border focus:ring-ring'
            } rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all`}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-600 text-secondary-foreground">
              Fjalëkalimi
            </label>
            <button
              type="button"
              className="text-xs text-primary hover:text-primary/80 font-600 transition-colors"
            >
              Harruat fjalëkalimin?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Fjalëkalimi është i detyrueshëm',
                minLength: { value: 6, message: 'Minimumi 6 karaktere' },
              })}
              placeholder="••••••••"
              className={`w-full px-3 py-2.5 pr-10 bg-input border ${
                errors.password ? 'border-red-500/50' : 'border-border'
              } rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Fshih fjalëkalimin' : 'Shfaq fjalëkalimin'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            {...register('rememberMe')}
            className="rounded border-border"
          />
          <label htmlFor="rememberMe" className="text-sm text-secondary-foreground cursor-pointer">
            Mbaj mend për 30 ditë
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground font-700 rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed mt-2"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Duke u kyçur...
            </>
          ) : (
            <>
              <LogIn size={16} />
              Hyr në Llogari
            </>
          )}
        </button>
      </form>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">ose</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Google OAuth */}
      <button className="w-full flex items-center justify-center gap-2.5 py-2.5 bg-muted border border-border rounded-xl text-sm font-600 text-foreground hover:bg-border active:scale-[0.98] transition-all duration-150">
        <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Vazhdo me Google
      </button>

      {/* Switch to register */}
      <p className="text-center text-sm text-secondary-foreground">
        Nuk keni llogari?{' '}
        <button
          onClick={onSwitchToRegister}
          className="text-primary font-700 hover:text-primary/80 transition-colors"
        >
          Regjistrohu falas
        </button>
      </p>

      {/* Demo Credentials Box */}
      <div className="mt-4 p-4 bg-muted/50 rounded-xl border border-border">
        <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-3">
          Llogaritë Demo
        </p>
        <div className="space-y-2">
          {DEMO_CREDENTIALS.map((cred) => (
            <div
              key={`demo-${cred.role}`}
              className="flex items-center justify-between gap-2 py-2 px-3 bg-card rounded-lg border border-border hover:border-primary/30 transition-all cursor-pointer group"
              onClick={() => handleUseCredential(cred.email, cred.password)}
            >
              <div className="min-w-0">
                <p className="text-xs font-700 text-foreground">{cred.role}</p>
                <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(cred.email, `email-${cred.role}`);
                }}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all shrink-0 opacity-0 group-hover:opacity-100"
                aria-label={`Kopjo email-in e ${cred.role}`}
              >
                {copiedField === `email-${cred.role}` ? (
                  <Check size={12} className="text-green-400" />
                ) : (
                  <Copy size={12} />
                )}
              </button>
              <span className="text-xs text-primary font-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                Përdor →
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Kliko një rresht për të mbushur automatikisht fushat
        </p>
      </div>
    </div>
  );
}